import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { REPORTSB_API_URL } from "config";

// reports-b "Customise Layout" persistence (per-tenancy, inherited up the
// parent chain then tenancy 0). Backed by the reporting service's
// /dashboard/layout endpoints. Mirrors dashboard.service auth handling.
const baseQuery = fetchBaseQuery({
    baseUrl: REPORTSB_API_URL,
    prepareHeaders: (headers) => {
        const user = localStorage.getItem('user');
        if (user) {
            const token = JSON.parse(user).token;
            if (token) headers.set('authorization', `Bearer ${token}`);
        }
        return headers;
    },
    credentials: "omit"
});

export const reportLayoutApi = createApi({
    reducerPath: "reportLayoutApi",
    baseQuery,
    tagTypes: ['ExportTemplates'],
    endpoints: (builder) => ({
        // Effective (inheritance-resolved) layout for every page of a tenancy:
        // { tenancyId, pages: { summary|sims|courses: { page, hidden[], templates[], source } } }
        getLayout: builder.query({
            query: (tenancyId) => ({ url: `dashboard/layout/${tenancyId}`, method: 'GET' }),
        }),
        // Save one page's layout for the tenancy. body: { page, hidden[], templates[] }
        saveLayout: builder.mutation({
            query: ({ tenancyId, page, hidden, templates }) => ({
                url: `dashboard/layout/${tenancyId}`,
                method: 'PUT',
                body: { page, hidden, templates },
            }),
        }),
        // Reset one page back to inherited (removes this tenancy's record).
        resetLayout: builder.mutation({
            query: ({ tenancyId, page }) => ({
                url: `dashboard/layout/${tenancyId}/${page}`,
                method: 'DELETE',
            }),
        }),
        // The tenancy's saved export-builder templates (tenancy-owned, NOT
        // inherited): { tenancyId, templates: [{ id, name, title, format,
        // period, sections[], drill[] }] }
        getExportTemplates: builder.query({
            query: (tenancyId) => ({ url: `dashboard/export-templates/${tenancyId}`, method: 'GET' }),
            providesTags: (result, error, tenancyId) => [{ type: 'ExportTemplates', id: tenancyId }],
        }),
        // Save the tenancy's template list wholesale (add + delete both send
        // the full next list, mirroring how layout presets persist).
        saveExportTemplates: builder.mutation({
            query: ({ tenancyId, templates }) => ({
                url: `dashboard/export-templates/${tenancyId}`,
                method: 'PUT',
                body: { templates },
            }),
            invalidatesTags: (result, error, { tenancyId }) => [{ type: 'ExportTemplates', id: tenancyId }],
        }),
    }),
});

export const {
    useGetLayoutQuery, useSaveLayoutMutation, useResetLayoutMutation,
    useGetExportTemplatesQuery, useSaveExportTemplatesMutation,
} = reportLayoutApi;
